"use client";
import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { ErrorAlert } from "@/components/ui/errorAlert";
import { OTPInput, REGEXP_ONLY_DIGITS, SlotProps } from "input-otp";
import { cn } from "@/lib/utils";
import { VITE_OTP_GATEWAY_URL } from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { secureInput } from "@/lib/security";
import { PhoneIcon } from "@phosphor-icons/react";

export const PhoneValidation = () => {
	const { formValues, setFormValue, setFormError, formErrors } = useFormDataStore();
	const {
		setFormErrors,
		setPhoneVerified,
		setValidateFormDetails,
		hasAttemptedSubmit,
		isPhoneVerified,
	} = useFormStateStore();

	const { phone } = formValues;
	const [errors, setErrors] = useState({
		phone: false,
	});

	const [errorMsg, setErrMsg] = useState("");
	const [code, setCode] = useState("");
	const [txId, setTxId] = useState("");

	const handlePhoneChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const rawValue = e.target.value.replace(/[^0-9]/g, "");

			const { sanitized, isValid } = secureInput.phone(rawValue);

			setFormValue("phone", sanitized);

			setErrors((prev) => ({ ...prev, phone: !isValid }));

			if (rawValue.trim() === "") {
				setFormError("phone", "Phone number is required");

				return;
			}

			if (!isValid) {
				setFormError("phone", "Incorrect format");

				return;
			}
		},
		[setFormValue, setFormError, setErrors],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			phone: !secureInput.phone(phone).isValid,
		};

		setErrors(newErrors);

		if (phone.trim() === "") {
			setFormError("phone", "Phone number is required");
		}

		if (newErrors.phone) {
			setFormError("phone", "Incorrect format");
		}

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [phone]);

	const sendOTP = useMutation({
		mutationKey: [phone],
		mutationFn: async () => {
			const res = await fetchOTP(phone);
			return res;
		},
		onSuccess: async (data) => {
			setTxId(data.id);
		},
		onError: async (err: any) => {
			setErrMsg(err.message);
		},
	});

	const confirmOTP = useMutation({
		mutationKey: [txId, code],
		mutationFn: async () => {
			if (!txId || !code) {
				throw new Error("OTP code is required for verification.");
			}
			const res = await verifyOTP(txId, code);
			return res;
		},
		onSuccess(data) {
			if (data.status == 200) {
				setPhoneVerified(true);
			}
		},
		onError: (err: any) => {
			setErrMsg(err.message);
		},
	});

	const handleChange = (code: string) => {
		setCode(code);
	};

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	return (
		<div className="yF-py-4 yF-space-y-4">
			<div>
				<label
					htmlFor="phone-number"
					className="yF-block yF-text-base yF-font-medium yF-leading-6 yF-text-gray-900"
				>
					Phone Number
				</label>
				<div className="yF-relative yF-mt-2 yF-rounded-md yF-shadow-sm">
					<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
						<PhoneIcon
							className="yF-h-5 yF-w-5 yF-text-gray-400"
							weight="fill"
							aria-hidden="true"
						/>
					</div>
					<input
						name="phone-number"
						id="phone-number"
						placeholder="04XX-XXX-XXX"
						aria-describedby="phone-number"
						aria-label="Phone Number"
						inputMode="numeric"
						autoComplete="tel"
						maxLength={10}
						onFocus={() => handleFocus("phone")}
						value={phone} // Ensure controlled component updates correctly
						onChange={(e) => {
							handlePhoneChange(e);
						}}
						className={clsx(
							errors.phone ||
								sendOTP.isError ||
								(hasAttemptedSubmit && formErrors.phone && !isPhoneVerified)
								? "yF-ring-red-500 yF-ring-1 focus:yF-ring-red-500"
								: "focus:yF-ring-[--yF-theme-color]",
							"yF-block yF-w-full yF-rounded-md yF-border-0 yF-py-2.5 yF-pl-10 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-inset yF-ring-gray-300 placeholder:yF-text-gray-400 focus:yF-ring-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] sm:yF-text-md sm:yF-leading-6",
						)}
					/>
				</div>
				{formErrors?.phone && (
					<span className="yF-text-xs yF-text-red-500">{formErrors.phone}</span>
				)}
			</div>

			<div className="yF-mt-5 sm:yF-mt-6">
				{sendOTP.isSuccess ? (
					""
				) : (
					<button
						className={clsx(
							sendOTP.isPending
								? "yF-bg-gray-200 hover:yF-cursor-not-allowed yF-text-gray-500 yF-ring-gray-100 hover:yF-bg-gray-200"
								: "yF-bg-[--yF-theme-color] hover:yF-bg-[--yF-hover-color]",
							"yF-inline-flex yF-w-full yF-justify-center yF-rounded-md yF-px-3 yF-py-2 yF-text-md yF-font-semibold yF-text-white yF-shadow-sm yF-duration-200 focus-visible:yF-outline focus-visible:yF-outline-1 focus-visible:yF-outline-offset-1 focus-visible:yF-outline-gray-900",
						)}
						disabled={sendOTP.isPending}
						onClick={(e) => {
							// Verify phone number
							e.preventDefault();

							if (phone.trim() === "") {
								setFormError("phone", "Phone number is required");
							}
							if (phone && !errors.phone) {
								sendOTP.mutate();
							}
						}}
					>
						{sendOTP.isPending ? (
							<div
								role="status"
								className="yF-flex yF-flex-col yF-w-full yF-h-full yF-justify-center yF-items-center"
							>
								<svg
									aria-hidden="true"
									className="yF-w-8 yF-h-8 yF-text-[--yF-theme-color] yF-animate-spin yF-fill-white"
									viewBox="0 0 100 101"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
										fill="currentColor"
									/>
									<path
										d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
										fill="currentFill"
									/>
								</svg>
								<span className="yF-sr-only">Loading...</span>
							</div>
						) : (
							"Send Verification Code"
						)}
					</button>
				)}
			</div>
			{/* Verify Code */}
			{sendOTP.isError && <ErrorAlert text={errorMsg} />}
			{sendOTP.isSuccess && (
				<>
					<div>
						<label
							htmlFor="verifyOTP"
							className="yF-block yF-text-base yF-font-medium yF-leading-6 yF-text-gray-900 yF-mb-1"
						>
							Verification Code
						</label>
						<div className="yF-group yF-flex yF-flex-row yF-items-center yF-justify-center">
							<OTPInput
								id="verifyOTP"
								onChange={handleChange}
								value={confirmOTP.error ? "" : code}
								maxLength={4}
								autoComplete="true"
								containerClassName="yF-text-gray-900 yF-group yF-flex yF-items-center has-[:disabled]:yF-opacity-30"
								render={({ slots }) => (
									<>
										<div className="yF-flex">
											{slots.slice(0, 2).map((slot, idx) => (
												<Slot key={idx} {...slot} />
											))}
										</div>

										<FakeDash />

										<div className="yF-flex">
											{slots.slice(2).map((slot, idx) => (
												<Slot key={idx} {...slot} />
											))}
										</div>
									</>
								)}
								inputMode="numeric"
								pattern={REGEXP_ONLY_DIGITS}
								autoFocus
							/>
						</div>
						{confirmOTP.isError && <ErrorAlert text={errorMsg} />}
					</div>
					<button
						className={clsx(
							confirmOTP.isPending
								? "yF-bg-gray-200 hover:yF-cursor-not-allowed yF-text-gray-500 yF-ring-gray-100 hover:yF-bg-gray-200"
								: "yF-bg-[--yF-theme-color] hover:yF-bg-[--yF-hover-color]",
							"yF-inline-flex yF-w-full yF-justify-center yF-rounded-md yF-px-3 yF-py-2 yF-text-md yF-font-semibold yF-text-white yF-shadow-sm yF-duration-200 focus-visible:yF-outline focus-visible:yF-outline-2 focus-visible:yF-outline-offset-2 focus-visible:yF-outline-gray-900",
						)}
						type="button"
						disabled={confirmOTP.isPending}
						onClick={async (e) => {
							e.preventDefault();
							confirmOTP.error ? setCode("") : confirmOTP.mutate();
						}}
					>
						{confirmOTP.isPending ? (
							<div
								role="status"
								className="yF-flex yF-flex-col yF-w-full yF-h-full yF-justify-center yF-items-center"
							>
								<svg
									aria-hidden="true"
									className="yF-w-8 yF-h-8 yF-text-[--yF-theme-color] yF-animate-spin yF-fill-white"
									viewBox="0 0 100 101"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
										fill="currentColor"
									/>
									<path
										d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
										fill="currentFill"
									/>
								</svg>
								<span className="yF-sr-only">Loading...</span>
							</div>
						) : (
							"Verify Code"
						)}
					</button>
				</>
			)}
		</div>
	);
};

async function fetchOTP(phone: string) {
	try {
		const response = await fetch(`${VITE_OTP_GATEWAY_URL}/api/send-otp`, {
			method: "POST",
			headers: {
				"content-type": "application/json; charset=utf-8",
			},
			body: JSON.stringify({ phone }),
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw errorData; //Pass the blockTime to catch error
		}

		const data = await response.json();
		return data;
	} catch (err: any) {
		if (err.blockTime) {
			const blockTimeMs = err.blockTime ?? 0;

			// Convert milliseconds to hours, minutes, and seconds
			const hours = Math.floor(blockTimeMs / (1000 * 60 * 60));
			const minutes = Math.floor((blockTimeMs % (1000 * 60 * 60)) / (1000 * 60));
			const seconds = Math.floor((blockTimeMs % (1000 * 60)) / 1000);

			const hoursText = hours === 1 ? `${hours} hour` : `${hours} hours`;
			const minutesText = minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
			const secondsText = seconds === 1 ? `${seconds} second` : `${seconds} seconds`;

			const formattedErrorMessage = `You are temporarily blocked due to excessive requests. Please try again after ${
				hours === 0
					? ""
					: hoursText +
						((minutes > 0 && seconds === 0) || (seconds > 0 && minutes === 0)
							? " and "
							: minutes === 0 && seconds === 0
								? ""
								: ", ")
			}${
				minutes === 0
					? ""
					: minutesText +
						(hours > 0 && seconds > 0 ? ", and " : seconds > 0 ? " and " : "")
			}${seconds === 0 ? "" : secondsText}.`;

			throw new Error(formattedErrorMessage);
		}

		// Custom error message from gateway
		if (err.msgStatus) {
			const { error } = err;
			throw new Error(error);
		}

		// Sinch has denied the number.
		if (err.error?.errorCode) {
			const { error, reason } = err;
			console.error(`Error[${error.errorCode}]: ${reason.status} - ${reason.reason}`);
			throw new Error(
				`Your phone number has been temporarily blocked due to excessive requests. Please try again later or use a different number.`,
			);
		}

		throw new Error("There has been an error. Please try again later.");
	}
}

async function verifyOTP(txId: string, code: string) {
	try {
		const response = await fetch(`${VITE_OTP_GATEWAY_URL}/api/verify-otp`, {
			method: "PUT",
			headers: {
				"content-type": "application/json; charset=utf-8",
			},
			body: JSON.stringify({ txId, code }),
		});

		const data = await response.json();

		if (data.status != 200) {
			throw data;
		}

		return data;
	} catch (err: any) {
		if (err.error?.status === "FAIL" && err.error.reason === "Expired") {
			throw new Error(`OTP has expired. Please request for a new verification code.`);
		} else if (err.error?.errorCode) {
			throw new Error("Invalid OTP code. Please try again.");
		} else {
			const errorMessage =
				err.message ||
				err.error?.message ||
				err.reason?.message ||
				JSON.stringify(err) || // Convert unknown objects to string safely
				"An unexpected error occurred. Please try again.";
			console.error("Unexpected Error: ", errorMessage);
			throw new Error("An unexpected error occurred in validating the OTP.");
		}
	}
}

function Slot(props: SlotProps) {
	return (
		<div
			className={cn(
				"yF-relative yF-w-10 yF-h-14 yF-text-[2rem]",
				"yF-flex yF-items-center yF-justify-center",
				"yF-transition-all yF-duration-300",
				"yF-border yF-border-y yF-border-r first:yF-border-l first:yF-rounded-l-md last:yF-rounded-r-md",
				"yF-group-hover:yF-border-[--yF-theme-color] group-focus-within:yF-border-[--yF-theme-color] group-focus-within:yF-border-1",
				"yF-outline yF-outline-0 yF-outline-[--yF-theme-color]",
				{
					"yF-outline-2 yF-outline-[--yF-theme-color]": props.isActive,
				},
			)}
		>
			{props.char !== null && <div>{props.char}</div>}
			{props.hasFakeCaret && <FakeCaret />}
		</div>
	);
}

function FakeCaret() {
	return (
		<div className="yF-absolute yF-pointer-events-none yF-inset-0 yF-flex yF-items-center yF-justify-center yF-animate-caret-blink">
			<div className="yF-w-px yF-h-8 yF-bg-white" />
		</div>
	);
}

function FakeDash() {
	return (
		<div className="yF-flex yF-w-10 yF-justify-center yF-items-center">
			<div className="yF-w-3 yF-h-1 yF-rounded-full yF-bg-border yF-bg-[--yF-theme-color]" />
		</div>
	);
}
